import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { errMsg } from "@/lib/api";
import { toast } from "sonner";
import { Mail, Plus, Inbox } from "lucide-react";

const STATUS_STYLES = {
  open: "bg-blue-500/15 text-blue-400 border-blue-500/30",
  pending: "bg-amber-500/15 text-amber-400 border-amber-500/30",
  resolved: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30",
  closed: "bg-slate-500/15 text-slate-400 border-slate-500/30",
};
const PRIORITY_STYLES = {
  urgent: "text-red-400",
  high: "text-orange-400",
  medium: "text-blue-400",
  low: "text-slate-400",
};

function Badge({ children, className }) {
  return (
    <span className={`inline-block px-2 py-0.5 rounded border text-[11px] font-mono-digits uppercase tracking-wider ${className}`}>
      {children}
    </span>
  );
}

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [tickets, setTickets] = useState([]);
  const [agents, setAgents] = useState([]);
  const [filter, setFilter] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ subject: "", description: "", requester_email: "", requester_name: "", priority: "medium", assignee_id: "" });

  const load = async () => {
    try {
      const [s, t, a] = await Promise.all([
        api.get("/stats"),
        api.get("/tickets", { params: filter ? { status: filter } : {} }),
        api.get("/agents"),
      ]);
      setStats(s.data);
      setTickets(t.data);
      setAgents(a.data);
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

  const createTicket = async (e) => {
    e.preventDefault();
    try {
      await api.post("/tickets", { ...form, assignee_id: form.assignee_id || null });
      toast.success("Ticket created");
      setShowForm(false);
      setForm({ subject: "", description: "", requester_email: "", requester_name: "", priority: "medium", assignee_id: "" });
      load();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  const cards = stats
    ? [
        { label: "Open tickets", value: stats.open_tickets, testid: "stat-open-tickets" },
        { label: "Active tasks", value: stats.active_tasks, testid: "stat-active-tasks" },
        { label: "Hours logged", value: stats.hours_logged, testid: "stat-hours-logged" },
        { label: "Billable due", value: `R${stats.billable_amount.toLocaleString()}`, testid: "stat-billable-amount" },
      ]
    : [];

  return (
    <div className="space-y-8" data-testid="tickets-dashboard">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight">Tickets</h1>
          <p className="text-sm text-muted-foreground mt-1">Every inbound email becomes a ticket here.</p>
        </div>
        <button
          data-testid="create-ticket-button"
          onClick={() => setShowForm(!showForm)}
          className="flex items-center gap-2 rounded-md bg-orange-500 hover:bg-orange-600 active:scale-[0.98] text-white text-sm font-semibold px-4 py-2.5 transition-all"
        >
          <Plus className="h-4 w-4" /> New ticket
        </button>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {cards.map((c) => (
          <div key={c.label} className="rounded-lg border border-border bg-card p-5 hover:border-orange-500/40 transition-colors">
            <p className="font-mono-digits text-[10px] uppercase tracking-widest text-muted-foreground">{c.label}</p>
            <p data-testid={c.testid} className="font-mono-digits text-2xl sm:text-3xl font-bold mt-2 tabular-nums">{c.value}</p>
          </div>
        ))}
      </div>

      {showForm && (
        <form data-testid="create-ticket-form" onSubmit={createTicket} className="rounded-lg border border-orange-500/30 bg-card p-5 space-y-4">
          <div className="grid sm:grid-cols-2 gap-4">
            <input required placeholder="Subject" value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })}
              data-testid="ticket-subject-input"
              className="rounded-md bg-secondary/60 border border-border px-3 py-2 text-sm outline-none focus:border-orange-500/60" />
            <input placeholder="Requester email" value={form.requester_email} onChange={(e) => setForm({ ...form, requester_email: e.target.value })}
              data-testid="ticket-requester-input"
              className="rounded-md bg-secondary/60 border border-border px-3 py-2 text-sm outline-none focus:border-orange-500/60" />
            <select value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value })}
              data-testid="ticket-priority-select"
              className="rounded-md bg-secondary/60 border border-border px-3 py-2 text-sm outline-none focus:border-orange-500/60">
              {["low", "medium", "high", "urgent"].map((p) => <option key={p} value={p}>{p}</option>)}
            </select>
            <select value={form.assignee_id} onChange={(e) => setForm({ ...form, assignee_id: e.target.value })}
              data-testid="ticket-assignee-select"
              className="rounded-md bg-secondary/60 border border-border px-3 py-2 text-sm outline-none focus:border-orange-500/60">
              <option value="">Unassigned</option>
              {agents.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
            </select>
          </div>
          <textarea placeholder="Description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
            data-testid="ticket-description-input" rows={3}
            className="w-full rounded-md bg-secondary/60 border border-border px-3 py-2 text-sm outline-none focus:border-orange-500/60" />
          <button type="submit" data-testid="ticket-submit-button"
            className="rounded-md bg-orange-500 hover:bg-orange-600 text-white text-sm font-semibold px-4 py-2 transition-colors">
            Create ticket
          </button>
        </form>
      )}

      <div className="flex gap-2 flex-wrap">
        {["", "open", "pending", "resolved", "closed"].map((s) => (
          <button key={s} onClick={() => setFilter(s)} data-testid={`filter-${s || "all"}`}
            className={`px-3 py-1.5 rounded-md text-xs font-mono-digits uppercase tracking-wider border transition-colors ${
              filter === s ? "border-orange-500/50 text-orange-400 bg-orange-500/10" : "border-border text-muted-foreground hover:text-foreground"
            }`}>
            {s || "all"}
          </button>
        ))}
      </div>

      <div className="space-y-2">
        {tickets.length === 0 && (
          <div className="rounded-lg border border-dashed border-border p-10 text-center text-muted-foreground">
            <Inbox className="h-8 w-8 mx-auto mb-3 opacity-50" />
            <p className="text-sm">No tickets yet. Send an email to your inbound address or create one manually.</p>
          </div>
        )}
        {tickets.map((t) => (
          <Link key={t.id} to={`/tickets/${t.id}`} data-testid={`ticket-row-${t.number}`}
            className="flex items-center gap-4 rounded-lg border border-border bg-card px-4 py-3.5 hover:border-orange-500/40 hover:shadow-lg hover:shadow-orange-500/5 transition-all">
            <span className="font-mono-digits text-xs text-muted-foreground shrink-0">{t.number}</span>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-semibold truncate">{t.subject}</p>
              <p className="text-xs text-muted-foreground truncate mt-0.5">
                {t.requester_name || t.requester_email || "internal"} · {new Date(t.created_at).toLocaleDateString()}
              </p>
            </div>
            {["email", "gmail"].includes(t.source) && (
              <span className="hidden sm:flex items-center gap-1 text-[10px] font-mono-digits uppercase tracking-wider text-sky-400">
                <Mail className="h-3 w-3" /> email
              </span>
            )}
            <span className={`text-[11px] font-mono-digits uppercase tracking-wider ${PRIORITY_STYLES[t.priority] || ""}`}>{t.priority}</span>
            <Badge className={STATUS_STYLES[t.status] || ""}>{t.status}</Badge>
          </Link>
        ))}
      </div>
    </div>
  );
}
